import {
  BadgeCheck,
  BarChart3,
  Bot,
  CalendarDays,
  CloudDownload,
  Copy,
  FileText,
  Globe,
  Mail,
  MailOpen,
  Contact,
  Send,
  Grid3x3,
  Hash,
  Images,
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  Library,
  LifeBuoy,
  Lightbulb,
  ListTodo,
  PiggyBank,
  Plug,
  Share2,
  Repeat,
  Settings,
  Sparkles,
  SquareKanban,
  Tags,
  Umbrella,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';

// Menu lateral e títulos do header. Fica fora do DashboardLayout para o teste de cobertura da
// documentação (src/docs/docs.coverage.test.ts) conferir que TODA tela do menu tem documento.

export type BaseMenuItem = {
  label: string;
  icon: LucideIcon;
  key?: string;
  permission?: string | null;
  /** Visível se o usuário tiver QUALQUER uma destas permissões (OR). */
  anyPermission?: string[];
  /** Exige ser gestor (hierarquia do cadastro) ou admin, além da permissão. */
  requiresTeamManager?: boolean;
};

export type LinkMenuItem = BaseMenuItem & {
  path: string;
  /** Ativo só na rota exata (ex.: /media não acende em /media/duplicates). */
  exact?: boolean;
};

export type ParentMenuItem = BaseMenuItem & {
  children: LinkMenuItem[];
};

export type MenuItem = LinkMenuItem | ParentMenuItem;

export const MENU_ITEMS: MenuItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', permission: 'dashboard.read' },
  {
    label: 'Atividades',
    icon: LayoutList,
    key: 'activities',
    anyPermission: ['activities.read', 'recurrences.read', 'coverage.manage'],
    children: [
      { label: 'Kanban', icon: SquareKanban, path: '/kanban', permission: 'activities.read' },
      { label: 'Calendário', icon: CalendarDays, path: '/calendar', permission: 'activities.read' },
      { label: 'Lista', icon: ListTodo, path: '/activities', permission: 'activities.read' },
      { label: 'Recorrência', icon: Repeat, path: '/recurrences', permission: 'recurrences.read' },
      { label: 'Cobertura', icon: Umbrella, path: '/coverage', permission: 'coverage.manage', requiresTeamManager: true },
    ],
  },
  {
    label: 'Mídias',
    icon: Images,
    key: 'media',
    anyPermission: ['media.read', 'media.import.manage'],
    children: [
      { label: 'Biblioteca', icon: Library, path: '/media', permission: 'media.read', exact: true },
      { label: 'Duplicadas', icon: Copy, path: '/media/duplicates', permission: 'media.read' },
      { label: 'Eventos e tags', icon: Tags, path: '/media/taxonomy', permission: 'media.read' },
      { label: 'Importação', icon: CloudDownload, path: '/media/import', permission: 'media.import.manage' },
    ],
  },
  {
    label: 'Redes sociais',
    icon: Share2,
    key: 'social',
    anyPermission: ['social.read'],
    children: [
      { label: 'Grade', icon: Grid3x3, path: '/social/grid', permission: 'social.read' },
      { label: 'Calendário de posts', icon: CalendarDays, path: '/social/calendar', permission: 'social.read' },
      { label: 'Posts', icon: FileText, path: '/social/posts', permission: 'social.read' },
      { label: 'Aprovações', icon: BadgeCheck, path: '/social/approvals', permission: 'social.read' },
      { label: 'Ideias e estratégia', icon: Lightbulb, path: '/social/ideas', permission: 'social.read' },
      { label: 'Hashtags', icon: Hash, path: '/social/hashtags', permission: 'social.read' },
      { label: 'Contas conectadas', icon: UserCog, path: '/social/accounts', permission: 'social.accounts.manage' },
    ],
  },
  { label: 'Landing pages', icon: Globe, path: '/landing-pages', permission: 'landing.read' },
  {
    label: 'E-mail marketing',
    icon: Mail,
    key: 'email',
    anyPermission: ['email.read', 'email.leads'],
    children: [
      { label: 'Campanhas', icon: MailOpen, path: '/email', permission: 'email.read', exact: true },
      { label: 'Leads', icon: Contact, path: '/email/leads', permission: 'email.leads' },
    ],
  },
  {
    label: 'Administração',
    icon: Settings,
    key: 'admin',
    permission: 'admin.menu',
    children: [
      { label: 'Equipe', icon: Users, path: '/admin/team', permission: 'team.manage' },
      { label: 'Quadros', icon: LayoutGrid, path: '/admin/boards', permission: 'boards.manage' },
      { label: 'Integrações', icon: Plug, path: '/admin/integrations', permission: 'integrations.manage' },
      { label: 'Contas de envio de e-mail', icon: Send, path: '/admin/email-senders', permission: 'email.configure' },
      { label: 'IA — Provedores', icon: Bot, path: '/admin/ai/providers', permission: 'ai.configure' },
      { label: 'IA — Agentes', icon: Sparkles, path: '/admin/ai/agents', permission: 'ai.configure' },
      { label: 'IA — Orçamento', icon: PiggyBank, path: '/admin/ai/budget', permission: 'ai.configure' },
      { label: 'IA — Consumo', icon: BarChart3, path: '/admin/ai/usage', permission: 'ai.configure' },
    ],
  },
  { label: 'Central de ajuda', icon: LifeBuoy, path: '/help' },
];

/** Títulos do header por prefixo de rota (o mais específico primeiro). */
export const PAGE_TITLES: [string, string][] = [
  ['/dashboard', 'Dashboard'],
  ['/kanban', 'Kanban'],
  ['/calendar', 'Calendário'],
  ['/activities', 'Atividades'],
  ['/recurrences', 'Recorrências'],
  ['/coverage', 'Cobertura de férias e folgas'],
  ['/media/duplicates', 'Mídias · Duplicadas'],
  ['/media/taxonomy', 'Mídias · Eventos e tags'],
  ['/media/import', 'Mídias · Importação'],
  ['/media', 'Mídias · Biblioteca'],
  ['/social/grid', 'Redes sociais · Grade'],
  ['/social/calendar', 'Redes sociais · Calendário de posts'],
  ['/social/posts', 'Redes sociais · Posts'],
  ['/social/approvals', 'Redes sociais · Aprovações'],
  ['/social/ideas', 'Redes sociais · Ideias e estratégia'],
  ['/social/hashtags', 'Redes sociais · Hashtags'],
  ['/social/accounts', 'Redes sociais · Contas conectadas'],
  ['/landing-pages', 'Landing pages'],
  ['/email/leads', 'E-mail marketing · Leads'],
  ['/email/templates', 'E-mail marketing · Template'],
  ['/email', 'E-mail marketing · Campanhas'],
  ['/admin/team', 'Equipe'],
  ['/admin/boards', 'Quadros'],
  ['/admin/integrations', 'Integrações'],
  ['/admin/email-senders', 'Contas de envio de e-mail'],
  ['/admin/ai/providers', 'IA · Provedores'],
  ['/admin/ai/agents', 'IA · Agentes e tom de voz'],
  ['/admin/ai/budget', 'IA · Orçamento'],
  ['/admin/ai/usage', 'IA · Consumo'],
  ['/help', 'Central de ajuda'],
];

/** Todas as telas do menu (folhas), na ordem do menu. */
export const menuLeaves = (items: MenuItem[] = MENU_ITEMS): LinkMenuItem[] =>
  items.flatMap((i) => ('children' in i ? i.children : [i]));
